// Reglas de la pantalla Cobranza: filtro y orden de asignaciones, lectura de la carga por cobrador,
// validación de los pesos del ranking y copy de la estrategia. Puras y sin React.
import type { AssignmentRuleResponse } from '@fiado/type-kit/bin/loanCollectorAssignment/dtos/responses/AssignmentRuleResponse';
import type { PriorityWeights } from '@fiado/type-kit/bin/loanCollectorAssignment/dtos/PriorityWeights';
import { AssignmentStatusEnum } from '@fiado/type-kit/bin/loanCollectorAssignment/enums/AssignmentStatusEnum';
import { AssignmentStrategyEnum } from '@fiado/type-kit/bin/loanCollectorAssignment/enums/AssignmentStrategyEnum';
import {
    ANY_FILTER,
    type AssignmentFiltersState,
    type AssignmentRow,
    type CollectorLoadRow,
    type DirectoryPresence,
    type FilterOption,
    type ResolvedLabel,
} from './types';

/** Orden en que la lista agrupa por estado: lo vigente arriba, lo cerrado abajo. */
const STATUS_ORDER: Record<AssignmentStatusEnum, number> = {
    [AssignmentStatusEnum.ACTIVE]: 0,
    [AssignmentStatusEnum.REASSIGNED]: 1,
    [AssignmentStatusEnum.INACTIVE]: 2,
};

/** Etiqueta es-MX de cada estrategia del motor de asignación. */
const STRATEGY_LABEL: Record<AssignmentStrategyEnum, string> = {
    [AssignmentStrategyEnum.BY_STORE]: 'Por tienda',
    [AssignmentStrategyEnum.BY_LOAD]: 'Por carga',
    [AssignmentStrategyEnum.BY_SENIORITY]: 'Por veteranía',
    [AssignmentStrategyEnum.ROUND_ROBIN]: 'Round robin',
};

/** Suma que deben dar los pesos del ranking ponderado; el lambda la exige (WeightsSumInvalidError). */
export const WEIGHTS_TOTAL = 100;

/** Los campos de una asignación contra los que busca el cuadro de texto. */
function searchableText(row: AssignmentRow): string {
    return [row.creditId, row.collectorUserId, row.collectorDisplayName ?? '', row.assignmentId]
        .join(' ')
        .toLowerCase();
}

/** Filtra por texto libre, estado, tienda y cobrador. `ANY_FILTER` deja pasar todo. */
export function filterAssignments(
    rows: readonly AssignmentRow[],
    filters: AssignmentFiltersState,
): AssignmentRow[] {
    const needle = filters.search.trim().toLowerCase();
    return rows.filter((row) => {
        if (needle !== '' && !searchableText(row).includes(needle)) return false;
        if (filters.status !== ANY_FILTER && row.status !== filters.status) return false;
        if (filters.storeId !== ANY_FILTER && row.storeId !== filters.storeId) return false;
        if (
            filters.collectorUserId !== ANY_FILTER &&
            row.collectorUserId !== filters.collectorUserId
        ) {
            return false;
        }
        return true;
    });
}

/** Cuántos filtros están puestos, para el conteo del encabezado de la columna. */
export function countActiveFilters(filters: AssignmentFiltersState): number {
    const picked = [filters.status, filters.storeId, filters.collectorUserId].filter(
        (value) => value !== ANY_FILTER,
    ).length;
    return picked + (filters.search.trim() === '' ? 0 : 1);
}

/** ACTIVE, REASSIGNED e INACTIVE en ese orden; dentro de cada grupo, lo más reciente primero. */
export function sortAssignments(rows: readonly AssignmentRow[]): AssignmentRow[] {
    return [...rows].sort((left, right) => {
        const byStatus = STATUS_ORDER[left.status] - STATUS_ORDER[right.status];
        if (byStatus !== 0) return byStatus;
        return right.assignedAt.localeCompare(left.assignedAt);
    });
}

/**
 * El tope que de verdad limita. El lambda exige un entero ≥ 1, así que un 0 o un decimal que llegue
 * por el wire NO es un tope: tomarlo como tal pondría «llegó a su tope» en todas las tarjetas.
 */
export function effectiveThreshold(threshold: number | null): number | null {
    if (threshold === null || !Number.isInteger(threshold) || threshold < 1) return null;
    return threshold;
}

/** `activeAssignments / threshold`; `null` cuando no hay tope — «sin tope» no es 0 ni 100 %. */
export function loadRatio(row: CollectorLoadRow): number | null {
    const threshold = effectiveThreshold(row.threshold);
    if (threshold === null) return null;
    return row.activeAssignments / threshold;
}

/** El cobrador llegó a su tope o lo pasó. Sin tope nunca está pasado. */
export function isOverThreshold(row: CollectorLoadRow): boolean {
    const threshold = effectiveThreshold(row.threshold);
    return threshold !== null && row.activeAssignments >= threshold;
}

/** Los que pasaron su tope arriba; dentro de cada grupo, la carga más alta primero. */
export function sortCollectorLoads(rows: readonly CollectorLoadRow[]): CollectorLoadRow[] {
    return [...rows].sort((left, right) => {
        const byThreshold = Number(isOverThreshold(right)) - Number(isOverThreshold(left));
        if (byThreshold !== 0) return byThreshold;
        return right.activeAssignments - left.activeAssignments;
    });
}

/**
 * La suma de los tres pesos, o `null` si la regla no los trae. El wire de Dev devuelve
 * `priorityWeights: {}`, y sumar ausentes daba `NaN` — que la pantalla leía como «no cumple».
 */
export function weightsSum(weights: PriorityWeights | null | undefined): number | null {
    if (!weights) return null;
    const parts = [weights.storeMatch, weights.loadAvailable, weights.seniority];
    if (!parts.every((part) => typeof part === 'number' && Number.isFinite(part))) return null;
    return parts[0] + parts[1] + parts[2];
}

/** La regla trae los tres pesos. Sin ellos no hay nada que validar: no es lo mismo que estar mal. */
export function hasWeights(weights: PriorityWeights | null | undefined): boolean {
    return weightsSum(weights) !== null;
}

/**
 * Los pesos suman exactamente 100. Es la MISMA regla cross-campo que el manager del lambda valida
 * con `WeightsSumInvalidError`: validar aquí evita quemar un request, pero el lambda sigue mandando.
 */
export function areWeightsValid(weights: PriorityWeights | null | undefined): boolean {
    return weightsSum(weights) === WEIGHTS_TOTAL;
}

export function describeStrategy(strategy: AssignmentStrategyEnum): string {
    return STRATEGY_LABEL[strategy];
}

/** La regla que el motor está usando hoy; `null` si el retailer todavía no activó ninguna. */
export function activeRule(
    rules: readonly AssignmentRuleResponse[],
): AssignmentRuleResponse | null {
    return rules.find((rule) => rule.active) ?? null;
}

/** Etiqueta es-MX de cada estado de una asignación; `INACTIVE` se lee «Cerrada» en la pantalla. */
const STATUS_LABEL: Record<AssignmentStatusEnum, string> = {
    [AssignmentStatusEnum.ACTIVE]: 'Activa',
    [AssignmentStatusEnum.REASSIGNED]: 'Reasignada',
    [AssignmentStatusEnum.INACTIVE]: 'Cerrada',
};

export function describeAssignmentStatus(status: AssignmentStatusEnum): string {
    return STATUS_LABEL[status];
}

/** Lo que se pinta donde iría el tope cuando la regla activa no puso ninguno. */
export const NO_THRESHOLD_LABEL = 'sin tope';

/** El id de cobrador no existe entre los usuarios de la cadena. */
export const UNKNOWN_COLLECTOR_NOTE = 'sin persona en la organización';

/** El id existe pero el directorio no devolvió su nombre; no es lo mismo que no existir. */
export const UNRESOLVED_NAME_NOTE = 'nombre sin resolver';

/** El directorio no respondió: nadie verificó si esa persona existe, así que no se afirma nada. */
export const DIRECTORY_DOWN_NOTE = 'directorio sin respuesta';

/** Nota de cada estado del directorio cuando no hay nombre que mostrar. */
const PRESENCE_NOTE: Record<DirectoryPresence, string> = {
    KNOWN: UNRESOLVED_NAME_NOTE,
    UNKNOWN: UNKNOWN_COLLECTOR_NOTE,
    UNAVAILABLE: DIRECTORY_DOWN_NOTE,
};

/** La tienda no está en el directorio de la cadena o el directorio no respondió. */
export const UNRESOLVED_STORE_NOTE = 'tienda sin resolver';

/**
 * Nombre del cobrador si el directorio lo resolvió; si no, el id con la leyenda que lo explica.
 * `presence` es obligatorio: sin él la pantalla acusaba de inexistente a quien nadie verificó.
 */
export function describeCollector(
    collectorUserId: string,
    displayName: string | null,
    presence: DirectoryPresence,
): ResolvedLabel {
    if (displayName !== null && displayName !== '') {
        return { text: displayName, note: null, isName: true };
    }
    return { text: collectorUserId, note: PRESENCE_NOTE[presence], isName: false };
}

/** Nombre de la tienda si el directorio lo resolvió; si no, el id con su leyenda. */
export function describeStore(storeId: string, storeName: string | null): ResolvedLabel {
    if (storeName !== null && storeName !== '') {
        return { text: storeName, note: null, isName: true };
    }
    return { text: storeId, note: UNRESOLVED_STORE_NOTE, isName: false };
}

/** Ordena opciones de filtro por etiqueta y quita duplicados de id. */
function toSortedOptions(entries: readonly [string, string][]): FilterOption[] {
    const byValue = new Map(entries);
    return [...byValue]
        .map(([value, label]) => ({ value, label }))
        .sort((left, right) => left.label.localeCompare(right.label, 'es-MX'));
}

/** Las tiendas que aparecen en las filas presentes; el filtro no ofrece nada que no esté en la lista. */
export function listAssignmentStores(rows: readonly AssignmentRow[]): FilterOption[] {
    return toSortedOptions(
        rows.map((row) => [row.storeId, describeStore(row.storeId, row.storeName).text]),
    );
}

/** Los cobradores que aparecen en las filas presentes, con el nombre resuelto o su id. */
export function listAssignmentCollectors(rows: readonly AssignmentRow[]): FilterOption[] {
    return toSortedOptions(
        rows.map((row) => [
            row.collectorUserId,
            describeCollector(row.collectorUserId, row.collectorDisplayName, row.collectorPresence)
                .text,
        ]),
    );
}

/** Lo que se pinta cuando el cobrador no cubre ninguna tienda. */
export const NO_COVERAGE_LABEL = 'Sin tiendas de cobertura';

/**
 * Las tiendas de cobertura de un cobrador. Con un nombre por tienda se listan los nombres; si el
 * directorio no resolvió todas se listan los ids, porque `storeNames` ya no dice cuál es cuál.
 */
export function describeCoverage(row: CollectorLoadRow): ResolvedLabel {
    if (row.storeIds.length === 0) return { text: NO_COVERAGE_LABEL, note: null, isName: false };
    if (row.storeNames.length === row.storeIds.length) {
        return { text: row.storeNames.join(' · '), note: null, isName: true };
    }
    return { text: row.storeIds.join(' · '), note: UNRESOLVED_STORE_NOTE, isName: false };
}

/** Lo que se pinta donde iría el usuario que creó o tocó el registro y el wire lo manda vacío. */
export const NO_ACTOR_LABEL = 'sin registro';

/** El usuario que hizo el cambio; en Dev el lambda manda `""` y un renglón vacío no dice nada. */
export function describeActor(actor: string | null | undefined): string {
    return actor === null || actor === undefined || actor.trim() === '' ? NO_ACTOR_LABEL : actor;
}

/** Lo tecleado en el tope, ya interpretado: `null` = sin tope, y `valid` dice si se puede mandar. */
export interface MaxLoadDraft {
    value: number | null;
    valid: boolean;
}

/**
 * El tope que el lambda acepta: entero ≥ 1 (`@IsInt()` + `@Min(1)` del type-kit). Vaciar el campo
 * es legítimo y significa «sin tope»; un 0 o un decimal se rechazan antes de quemar el request.
 */
export function parseMaxLoad(raw: string): MaxLoadDraft {
    const trimmed = raw.trim();
    if (trimmed === '') return { value: null, valid: true };
    const parsed = Number(trimmed);
    if (!Number.isInteger(parsed) || parsed < 1) return { value: null, valid: false };
    return { value: parsed, valid: true };
}
